import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import RestaurantDashboardWidget from "@/components/dashboard/RestaurantDashboardWidget";
import RestaurantMenuPanel from "@/components/dashboard/RestaurantMenuPanel";
import RestaurantOrdersPanel from "@/components/dashboard/RestaurantOrdersPanel";
import RestaurantOpenToggle from "@/components/dashboard/RestaurantOpenToggle";
import CuisineEditorCard from "@/components/dashboard/CuisineEditorCard";
import PushPermissionBanner from "@/components/PushPermissionBanner";
import SilentModeBanner from "@/components/SilentModeBanner";
import FullScreenAlertBanner from "@/components/FullScreenAlertBanner";
import KitchenBoard from "@/components/dashboard/KitchenBoard";
import MenuHealthScore from "@/components/dashboard/MenuHealthScore";
import StockoutAlerts from "@/components/dashboard/StockoutAlerts";
import OwnerReviewsManager from "@/components/OwnerReviewsManager";
import { apiClient } from "@/lib/api";
import { unlockAudio } from "@/lib/pushNotifications";
import { Navigate, useSearchParams } from "react-router-dom";
import { BarChart3, ChefHat, ShoppingBag, Sparkles, History, Settings, Star } from "lucide-react";

const VALID_TABS = ["today", "orders", "menu", "stats", "insights", "reviews", "settings"];

/** Restaurant work screen — lands directly on today's kitchen board (zero navigation).
 * Menu / revenue / settings stay reachable via the secondary tab bar below. */
export default function RestaurantDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const urlTab = params.get("tab");
  const [tab, setTab] = useState(VALID_TABS.includes(urlTab) ? urlTab : "today");

  // Deep link from a push notification (?tab=today) → open the right tab
  useEffect(() => {
    if (urlTab && VALID_TABS.includes(urlTab)) setTab(urlTab);
  }, [urlTab]);

  if (!user) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-mute">{t("dashboard.loading")}</div>;
  if (user.role === "admin") return <Navigate to="/dashboard/admin" replace />;
  if (user.role !== "restaurant") return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-mute">{t("admin.accessDenied")}</div>;

  const TABS = [
    { k: "today", label: t("rdash.tabToday"), icon: <ChefHat className="w-3.5 h-3.5" /> },
    { k: "orders", label: t("rdash.tabHistory"), icon: <History className="w-3.5 h-3.5" /> },
    { k: "menu", label: t("rdash.tabMenu"), icon: <ShoppingBag className="w-3.5 h-3.5" /> },
    { k: "stats", label: t("rdash.tabStats"), icon: <BarChart3 className="w-3.5 h-3.5" /> },
    { k: "insights", label: t("rdash.tabInsights"), icon: <Sparkles className="w-3.5 h-3.5" /> },
    { k: "reviews", label: t("rdash.tabReviews"), icon: <Star className="w-3.5 h-3.5" /> },
    { k: "settings", label: t("rdash.tabSettings"), icon: <Settings className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <PushPermissionBanner />
      <SilentModeBanner />
      <FullScreenAlertBanner />
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-red">{t("dashboard.restaurantSubtitle")}</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold">{t("dashboard.restaurantTitle")}</h1>
        </div>
      </div>

      {/* Top tab bar — everything one click away, "Commandes du jour" simply added as default */}
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar" data-testid="resto-tabs">
        {TABS.map((tt) => (
          <button
            key={tt.k}
            onClick={() => { unlockAudio(); setTab(tt.k); }}
            data-testid={`resto-tab-${tt.k}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border transition-colors whitespace-nowrap ${tab === tt.k ? "bg-ink text-white border-ink" : "bg-white text-ink border-sand hover:border-brand-red"}`}
          >
            {tt.icon} {tt.label}
          </button>
        ))}
      </div>

      {tab === "today" && <KitchenBoard />}
      {tab === "orders" && <RestaurantOrdersPanel />}
      {tab === "menu" && <RestaurantMenuPanel />}
      {tab === "stats" && <RestaurantDashboardWidget />}
      {tab === "insights" && (
        <div className="space-y-6">
          <MenuHealthScore />
          <StockoutAlerts />
        </div>
      )}
      {tab === "reviews" && <OwnerReviewsTab />}
      {tab === "settings" && (
        <div className="space-y-6">
          <RestaurantOpenToggle />
          <CuisineEditorCard />
        </div>
      )}
    </div>
  );
}

function OwnerReviewsTab() {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState(null);
  const load = () => apiClient.get("/my/restaurant/reviews").then(({ data }) => setReviews(data)).catch(() => setReviews([]));
  useEffect(() => { load(); }, []);
  if (reviews === null) return <div className="text-center text-mute py-10">{t("rdash.reviewsLoading")}</div>;
  return (
    <div data-testid="owner-reviews-tab">
      <p className="text-sm text-mute mb-4">
        {t("rdash.reviewsIntro")}
      </p>
      <OwnerReviewsManager reviews={reviews} onUpdate={() => load()} />
    </div>
  );
}
